import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import generatePDF from 'react-to-pdf';
import { Icon, faEye, faFilePdf, faUpload } from '../../Icon';
import { AppEntities, PassportData, PassportMileage } from '@/types';
import { useTranslate } from '@/translations';
import {
  dateConverter,
  getDocumentManagementPath,
  localDateFormatted,
  timeConverter,
} from '@/utils/helpers';
import { Col, Descriptions, Empty, Row, Space, Spin, Tabs } from 'antd';
import { AppPaths } from '@/routes';
import { StyledLink } from '@/ui/globalStyles';
import { useAppCustomers } from '@/hooks';
import { type MapHandle } from '../../Map';
import { VehicleImages, type VehicleImagesHandle } from '../VehicleImages';
import type { RepresentativeImageExportGuard } from '@/api/ProductImages/useRepresentativeImage';
import { VehicleInformation } from '../VehicleInformation';
import { LocationCard } from '../LocationCard';
import { LightBoxLink } from '@/ui/components/ImagesLightBox';
import { FORMAT_DATE_DAYJS } from '@/utils/constants';

const PASSPORT_DETAILS_LOADING_OVERLAY_ID =
  'passport-details-export-loading-overlay';

interface PassportDetailProps {
  passportData?: PassportData;
  showVinModal?: boolean;
  showImagesAndMap?: boolean;
  imagesActive?: boolean;
}
export const PassportDetails: FC<PassportDetailProps> = ({
  passportData,
  showVinModal,
  showImagesAndMap,
  imagesActive = true,
}) => {
  const { t } = useTranslate();
  const { hasAdminAccess, customerIds, locationIds, userPermissions } =
    useAppCustomers();
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const mapRef = useRef<MapHandle>(null);
  const imageRef = useRef<VehicleImagesHandle>(null);
  const pendingExport = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const [mapImageForPdf, setMapImageForPdf] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'located' | 'lastScan'>(
    (passportData?.lastLocateds?.length ?? 0) > 0 ? 'located' : 'lastScan',
  );

  const exportScope = JSON.stringify([
    passportData?.vehicleId,
    passportData?.customerId,
    passportData?.vin,
    passportData?.regNumber,
    customerIds,
    locationIds,
    userPermissions,
  ]);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useLayoutEffect(
    () => () => {
      if (showImagesAndMap) pendingExport.current?.abort();
    },
    [exportScope, imagesActive, showImagesAndMap],
  );

  if (!passportData) return <Empty />;

  const handleDownloadPdf = async () => {
    if (pendingExport.current || (showImagesAndMap && !imagesActive)) return;
    const passportDetailsElement = document.getElementById('passport-details');
    if (!passportDetailsElement) return;
    const controller = new AbortController();
    pendingExport.current = controller;
    let imageGuard: RepresentativeImageExportGuard | undefined;
    const cancel = () => controller.abort();
    const finish = () => {
      if (pendingExport.current !== controller) return;
      pendingExport.current = null;
      if (mounted.current) {
        setMapImageForPdf(null);
        setIsPdfExporting(false);
      }
    };
    controller.signal.addEventListener('abort', finish, { once: true });
    const isValid = () =>
      !controller.signal.aborted &&
      (!showImagesAndMap ||
        (passportDetailsElement.isConnected && imageGuard?.isValid()));
    setIsPdfExporting(true);
    try {
      let snapshotImage: string | null = null;
      if (showImagesAndMap) {
        imageGuard = await imageRef.current?.prepareExport();
        imageGuard?.signal.addEventListener('abort', cancel, { once: true });
        if (!isValid()) return;
        snapshotImage = (await mapRef.current?.takeSnapshot()) ?? null;
      }
      if (!isValid()) return;
      setMapImageForPdf(snapshotImage);
      await new Promise<void>((resolve) => {
        const done = () => {
          clearTimeout(timer);
          controller.signal.removeEventListener('abort', done);
          resolve();
        };
        const timer = setTimeout(done, 1000);
        controller.signal.addEventListener('abort', done, { once: true });
        if (controller.signal.aborted) done();
      });
      if (!isValid()) return;
      const filename = `passport-details-${passportData.vin || passportData.regNumber || 'vehicle'}.pdf`;
      const pdf = await generatePDF(() => passportDetailsElement, {
        filename,
        ...(showImagesAndMap ? { method: 'build' as const } : {}),
        page: { margin: 8 },
        overrides: {
          canvas: {
            onclone: (clonedDoc) => {
              const loadingOverlay = clonedDoc.getElementById(
                PASSPORT_DETAILS_LOADING_OVERLAY_ID,
              );
              loadingOverlay?.remove();
            },
          },
        },
      });
      // The renderer owns copied pixels; revoking the live blob cannot cancel its save.
      if (showImagesAndMap && isValid())
        await pdf.save(filename, { returnPromise: true });
    } catch (error) {
      if (!controller.signal.aborted) throw error;
    } finally {
      imageGuard?.signal.removeEventListener('abort', cancel);
      controller.signal.removeEventListener('abort', finish);
      finish();
    }
  };

  const rowItems = [
    {
      key: 0,
      label: t('vin').toUpperCase(),
      children: showVinModal ? (
        <VehicleInformation vin={passportData.vin} showAsLabel />
      ) : (
        passportData.vin
      ),
    },
    {
      key: 1,
      label: t('manufacturer'),
      children: passportData.manufacturer,
    },
    { key: 2, label: t('vehicle'), children: passportData.model },
    {
      key: 3,
      label: t('year'),
      children: passportData.manufactureDate
        ? passportData.manufactureDate.split('-')[0]
        : null,
    },
    { key: 4, label: t('colour'), children: passportData.colour },
    { key: 5, label: t('regNumber'), children: passportData.regNumber },
    { key: 6, label: t('site'), children: passportData.locationDesc },
    { key: 7, label: t('location'), children: passportData.location },
    { key: 8, label: t('status'), children: passportData.vinStatus },
    {
      key: 9,
      label: t('statusDescription'),
      children: passportData.vinStatusDesc,
    },
    {
      key: 10,
      label: t('manufacturedDate'),
      children: passportData.manufactureDate
        ? localDateFormatted(passportData.manufactureDate)
        : null,
    },
    {
      key: 11,
      label: t('transportCompany'),
      children: passportData.providerName,
    },
    {
      key: 12,
      label: t('arrivalDestination'),
      children: passportData.locationDesc,
    },
    {
      key: 13,
      label: t('arrivalPostCode'),
      children: passportData.locationPostCode,
    },
    {
      key: 14,
      label: t('fleetCode'),
      children: passportData.fleetCode,
    },
    {
      key: 15,
      label: t('lastMechanicalInspection'),
      children: dateConverter(passportData.lastMechanicalInspection),
    },
    {
      key: 16,
      label: t('siteArrivalDate'),
      children: dateConverter(passportData.siteArrivalDate),
    },
    {
      key: 17,
      label: t('firstArrivalDate'),
      children: dateConverter(passportData.firstArrivalDate),
    },
    {
      key: 18,
      label: t('otherInspections'),
      children: (
        <Space wrap>
          {passportData.otherInspections?.map((inspectionId) => (
            <StyledLink
              key={inspectionId}
              onClick={() =>
                window.open(`/${AppPaths.inspection}/${inspectionId}`, '_blank')
              }
            >
              {inspectionId}
            </StyledLink>
          ))}
        </Space>
      ),
    },
    {
      key: 27,
      label: t('files'),
      children: (
        <>
          <Link
            to={getDocumentManagementPath({
              entityReference: passportData.regNumber,
            })}
          >
            <Icon icon={faEye} />
          </Link>
          {hasAdminAccess && (
            <Link
              to={getDocumentManagementPath({
                modal: 'uploadDocument',
                entity: AppEntities.Vehicle,
                entityReference: passportData.regNumber,
              })}
              style={{ marginLeft: '8px' }}
            >
              <Icon icon={faUpload} />
            </Link>
          )}
        </>
      ),
    },
    {
      key: 28,
      label: t('functions'),
      children: (
        <span className="d-inline-flex">
          <Icon
            icon={faFilePdf}
            color="#1677ff"
            className="cursor-pointer"
            onClick={handleDownloadPdf}
          />
        </span>
      ),
    },
  ];

  if (passportData.customerId === 19) {
    const collectionHold = {
      key: 19,
      label: t('collectionHold'),
      children: dateConverter(passportData.collectionHold),
    };
    const deliveryHold = {
      key: 20,
      label: t('deliveryHold'),
      children: dateConverter(passportData.deliveryHold),
    };
    const financeApproved = {
      key: 21,
      label: t('financeApproved'),
      children: dateConverter(passportData.financeApproved),
    };
    const holdAtPort = {
      key: 22,
      label: t('holdAtPort'),
      children: dateConverter(passportData.holdAtPort),
    };
    const destinationDealerCode = {
      key: 23,
      label: t('destinationDealerCode'),
      children: dateConverter(passportData.dealerCode),
    };
    const destinationDealer = {
      key: 24,
      label: t('destinationDealer'),
      children: dateConverter(passportData.destinationDealer),
    };
    const destinationCountry = {
      key: 25,
      label: t('destinationCountry'),
      children: dateConverter(passportData.countryCode),
    };
    const lineOffLocation = {
      key: 26,
      label: t('lineOffLocation'),
      children: dateConverter(passportData.locationDesc),
    };

    rowItems.splice(19, 0, collectionHold);
    rowItems.splice(20, 0, deliveryHold);
    rowItems.splice(21, 0, financeApproved);
    rowItems.splice(22, 0, holdAtPort);
    rowItems.splice(23, 0, destinationDealerCode);
    rowItems.splice(24, 0, destinationDealer);
    rowItems.splice(25, 0, destinationCountry);
    rowItems.splice(26, 0, lineOffLocation);
    // rowItems.push(astonMartinHold.flat())
  }

  if (passportData.mileage !== undefined) {
    rowItems.splice(rowItems.findIndex((row) => row.key === 7) + 1, 0, {
      key: 29,
      label: t('mileage'),
      children: (
        <Mileage mileage={passportData.mileage} withPhoto={showImagesAndMap} />
      ),
    });
  }

  return (
    <div id="passport-details" className="pb-3 position-relative">
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={showImagesAndMap ? 12 : 24}>
          <Descriptions
            className="light-content"
            bordered
            size="small"
            column={1}
            items={rowItems}
          />
        </Col>
        {showImagesAndMap ? (
          <Col xs={24} lg={12}>
            <Row gutter={[16, 16]}>
              <Col span={24} style={{ height: 250 }}>
                <VehicleImages
                  ref={imageRef}
                  vehicleId={passportData.vehicleId}
                  images={passportData.images ?? []}
                  exteriorImageCount={passportData.exteriorImageCount}
                  enabled={imagesActive}
                  arrival={passportData.arrivals?.[0]}
                />
              </Col>
              <Col span={24} style={{ height: 300 }}>
                <Tabs
                  activeKey={activeTab}
                  onChange={(key) =>
                    setActiveTab(key as 'located' | 'lastScan')
                  }
                  items={[
                    {
                      key: 'located',
                      label: t('located'),
                      children: (
                        <LocationCard
                          entries={passportData.lastLocateds ?? []}
                          tabKind="located"
                          enableScroll={false}
                          mapRef={activeTab === 'located' ? mapRef : undefined}
                          mapImageForPdf={
                            activeTab === 'located' ? mapImageForPdf : null
                          }
                        />
                      ),
                    },
                    {
                      key: 'lastScan',
                      label: t('lastScan'),
                      children: (
                        <LocationCard
                          entries={passportData.lastScans ?? []}
                          tabKind="lastScan"
                          enableScroll={true}
                          mapRef={activeTab === 'lastScan' ? mapRef : undefined}
                          mapImageForPdf={
                            activeTab === 'lastScan' ? mapImageForPdf : null
                          }
                        />
                      ),
                    },
                  ]}
                />
              </Col>
            </Row>
          </Col>
        ) : null}
      </Row>
      {isPdfExporting ? (
        <div
          id={PASSPORT_DETAILS_LOADING_OVERLAY_ID}
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 20,
            background: 'rgba(255, 255, 255, 0.72)',
            backdropFilter: 'blur(1px)',
          }}
        >
          <Spin size="large" />
        </div>
      ) : null}
    </div>
  );
};

const Mileage: FC<{ mileage: PassportMileage | null; withPhoto?: boolean }> = ({
  mileage,
  withPhoto,
}) => {
  const { t } = useTranslate();
  if (!mileage) return null;
  const value = `${new Intl.NumberFormat('en-GB').format(mileage.value)} ${t('miles')}`;
  const sourceLabel = {
    Arrival: t('mileageSourceArrival'),
    ReturnInspection: t('mileageSourceReturnInspection'),
  }[mileage.source];
  const caption = [
    value,
    timeConverter(mileage.recordedAtUtc, FORMAT_DATE_DAYJS),
    sourceLabel,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <span>
      {withPhoto && mileage.image ? (
        <LightBoxLink
          image={{
            imageFileUrl: mileage.image.url,
            thumbnailImageUrl: mileage.image.resizedImageUrl,
            caption,
          }}
        >
          {value}
        </LightBoxLink>
      ) : (
        value
      )}
      {` (${timeConverter(mileage.recordedAtUtc, 'MMM DD')})`}
      {withPhoto && !mileage.image ? ` ${t('photoUnavailable')}` : null}
    </span>
  );
};
